import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { ArrowRight } from "lucide-react";
import Seo from "../components/Seo";

const ALL = "All";

export default function Resources() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(ALL);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/resources");
        setItems(data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const categories = useMemo(() => {
    const set = new Set(items.map((i) => i.category));
    return [ALL, ...Array.from(set)];
  }, [items]);

  const filtered = filter === ALL ? items : items.filter((i) => i.category === filter);

  return (
    <div data-testid="page-resources">
      <Seo
        title="Resources — White papers, field notes & guides"
        description="White papers, restoration-readiness guides, the Seed Production Area framework, articles on Caring for Country as restoration infrastructure."
        path="/resources"
      />

      {/* Hero */}
      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <p className="font-mono text-[11px] tracking-[0.28em] uppercase text-eucalypt/70 font-semibold flex items-center gap-3 mb-6">
            <span className="text-ochre">Library</span>
            <span className="w-8 h-px bg-eucalypt/30" aria-hidden="true" />
            <span>Resources</span>
          </p>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight leading-[1.02] max-w-4xl">
            White papers, field notes and the{" "}
            <span className="italic text-eucalypt">thinking behind the work.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-ink/85 leading-relaxed">
            Restoration readiness guides, seed shortage reports, framework documents and
            Caring for Country essays. Written for practitioners, funders and the people
            doing the work on Country.
          </p>
        </div>
      </section>

      {/* Filter chips */}
      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-wrap gap-2" data-testid="resources-filter">
            {categories.map((c) => {
              const active = filter === c;
              return (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`rounded-full text-[11px] tracking-[0.22em] uppercase font-mono font-semibold px-5 py-2.5 border transition-all hover:-translate-y-0.5 ${
                    active
                      ? "bg-eucalypt text-chalk border-eucalypt shadow-[0_8px_20px_-8px_rgba(44,74,59,0.5)]"
                      : "bg-transparent text-ink/80 border-rule hover:border-eucalypt hover:text-eucalypt"
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
          {loading ? (
            <p className="specimen-label">Loading resources…</p>
          ) : filtered.length === 0 ? (
            <p className="specimen-label" data-testid="resources-empty">
              No resources yet in this category.
            </p>
          ) : (
            <ul className="space-y-5" data-testid="resources-list">
              {filtered.map((r, i) => (
                <li key={r.id} data-testid={`resource-row-${i}`}>
                  <Link
                    to={`/resources/${r.id}`}
                    className="group grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-start rounded-2xl bg-card border border-rule p-5 md:p-6 hover:-translate-y-1 hover:shadow-[0_20px_48px_-16px_rgba(26,23,21,0.15)] transition-all"
                  >
                    {/* Date / category */}
                    <div className="md:col-span-2 flex md:flex-col gap-3 md:gap-1">
                      <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-eucalypt/70 font-semibold">
                        {new Date(r.created_at).toLocaleDateString("en-AU", {
                          year: "numeric",
                          month: "short",
                        })}
                      </span>
                      <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted">
                        {r.category}
                      </span>
                    </div>

                    {/* Cover image */}
                    <div className="md:col-span-3">
                      {r.cover_image ? (
                        <div className="rounded-xl overflow-hidden aspect-[4/3] bg-parchment/40">
                          <img
                            src={r.cover_image}
                            alt=""
                            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                            loading="lazy"
                          />
                        </div>
                      ) : (
                        <div className="rounded-xl aspect-[4/3] bg-parchment/40 flex items-center justify-center">
                          <span className="specimen-label">Article</span>
                        </div>
                      )}
                    </div>

                    {/* Body */}
                    <div className="md:col-span-6">
                      <h2 className="font-display text-2xl md:text-3xl leading-tight font-medium tracking-tight group-hover:text-eucalypt transition-colors">
                        {r.title}
                      </h2>
                      <p className="mt-3 text-base text-ink/70 leading-relaxed">{r.summary}</p>
                      {r.tags && r.tags.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-1.5">
                          {r.tags.map((t) => (
                            <span
                              key={t}
                              className="rounded-full border border-rule px-3 py-1 text-[10px] tracking-[0.18em] uppercase font-mono text-muted"
                            >
                              {t}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Chevron */}
                    <div className="md:col-span-1 text-right pt-1">
                      <ArrowRight
                        size={20}
                        className="inline-block opacity-30 group-hover:opacity-100 group-hover:translate-x-1 transition-all text-eucalypt"
                      />
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
